import {
    IDataTypeResponse,
    INodeOutputSensorDataConfigSlot,
} from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { queryClient } from 'utils/query';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';

export const DataTypeDataSourceKey = 'data-types';

export type DataTypeFilter = Readonly<{
    slot?: INodeOutputSensorDataConfigSlot;
    code?: string;
    name?: string;
    nameSearch?: string;
}>;

export type UseDataTypesDataSourceOptions = BaseDataSourceOptions<
    IDataTypeResponse,
    DataTypeFilter
>;

export type DataTypesPagedDataSource = PagedDataSource<IDataTypeResponse, DataTypeFilter>;

export type DataTypesInfiniteDataSource = InfiniteDataSource<
    IDataTypeResponse,
    DataTypeFilter
>;

const getAllDataTypes: DataSourceQuery<IDataTypeResponse, DataTypeFilter> = ({
    filter: { slot, code, name, nameSearch },
    sort,
    page,
    pageLimit,
}) => {
    const filterExpr = Filter.and(
        // Prefer more specific `code` filter when available
        code === undefined && slot !== undefined && !slot.SupportsAnyDataType
            ? Filter.in('Code', slot.SupportedDataTypes)
            : undefined,
        code !== undefined ? Filter.eq('Code', code) : undefined,
        name !== undefined ? Filter.eq('Name', name) : undefined,
        nameSearch !== undefined ? Filter.startsWith('Name', nameSearch) : undefined,
    );
    const orderBy = buildOrderBy(sort);

    return toDataSourceResult(
        sdClient.getDataTypes({
            filter: filterExpr,
            page,
            pageLimit,
            orderBy,
        }),
    );
};

export function usePagedDataTypesDataSource({
    ...options
}: UseDataTypesDataSourceOptions): DataTypesPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: getAllDataTypes,
    });
}

export function useInfiniteDataTypesDataSource({
    ...options
}: UseDataTypesDataSourceOptions): DataTypesInfiniteDataSource {
    return useInfiniteDataSource({
        ...options,
        query: getAllDataTypes,
    });
}

export function invalidateDataTypesDataSource() {
    queryClient.invalidateQueries({
        queryKey: [DataTypeDataSourceKey],
    });
}
